import { StreamStates, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { CameraIcon, CameraOffIcon, MicMutedIcon, MicUnmutedIcon } from "@minervaproject/fds";
import { useCallback, useEffect, useState } from "react";

import { CircleButton } from "./circle_button";

import { createDeviceErrorToast, createDeviceToast } from "components/shared/toasts";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import {
  setPttModeForUser,
  setUserIsPerformingAvCheck,
  toggleUserHandRaised,
} from "shared/reducers/classroom";
import { CapabilityType, KeyboardShortcutSignalType, LocalModalContentType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { BandwidthModeType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { setKeyboardShortcutSignal, showLocalModal } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";
import {
  selectCurrentUserhasVisibleParticipantAv,
  selectEffectivePttMode,
} from "store/selectors/classroom";

function AvControls() {
  const dispatch = useAppDispatch();
  const avConnection = useAvContext();
  const userId = useAppSelector(localSelectors.selectCurrentUserId());
  const streamState = avSelectors.useStreamState(userId);
  const user = useUserProperties(userId, ["bandwidth_mode"]);
  const bandwidthMode = user?.bandwidth_mode || BandwidthModeType.AutomaticBandwidth;
  const isAudioOnly =
    bandwidthMode === BandwidthModeType.AudioOnly ||
    bandwidthMode === BandwidthModeType.AudioOnlyForced;
  const { cameraError, micError } = avSelectors.useDeviceGlobalErrors();
  const forcedPttMode = useAppSelector(classroomSelectors.selectForcedPttMode(userId));

  const hasActiveBreakoutSession = !!useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const isClassInPttMode = useAppSelector(classroomSelectors.selectPttModeForClass());
  const allowStudentNoVideo = useAppSelector(classroomSelectors.selectAllowStudentNoVideo());
  const pttMode = useAppSelector(selectEffectivePttMode(userId));

  const isMutedBecauseNotVisible = !useAppSelector(selectCurrentUserhasVisibleParticipantAv());
  const [wasInBreakout, setWasInBreakout] = useState<boolean>(false);

  const canTogglePtt =
    !forcedPttMode &&
    !isMutedBecauseNotVisible &&
    (canControlClassroom || hasActiveBreakoutSession || !isClassInPttMode);

  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());

  const handleOpenAVPreferences = useCallback(() => {
    dispatch(setUserIsPerformingAvCheck({ userId, isPerformingAvCheck: true }));
    dispatch(showLocalModal({ content: LocalModalContentType.AvPreferences }));
  }, [userId, dispatch]);

  const handleRaiseHand = useCallback(() => {
    dispatch(toggleUserHandRaised({ userId }));
  }, [userId, dispatch]);

  // We want to show muted controls if devices are disabled or not available
  const mutedVideo = !streamState?.video_enabled || !streamState?.video_available;
  const mutedAudio = !streamState?.audio_enabled || !streamState?.audio_available;
  const isPublishedStreamLoading = streamState?.state !== StreamStates.playable;

  const camTooltip = `${
    !!cameraError || isAudioOnly
      ? "Disabled"
      : isPublishedStreamLoading
        ? "Loading"
        : mutedVideo
          ? "Enable"
          : "Disable"
  } camera`;
  const micTooltip = micError
    ? "Disabled microphone"
    : isPublishedStreamLoading
      ? "Loading microphone"
      : !mutedAudio
        ? "Mute microphone"
        : canTogglePtt
          ? "Unmute microphone"
          : "Unmuting disabled. Hold down the CONTROL key to temporarily unmute.";

  const createMicrophoneMutedToast = useCallback(
    (isNotVisible = false) => {
      const message = isNotVisible
        ? "You must be on screen to speak. Raise your hand to signal to the instructor that you'd like to speak."
        : "You're muted. Hold down the control key to temporarily unmute anytime.";
      createDeviceToast(
        "Microphone muted",
        message,
        false,
        "mic",
        isNotVisible ? handleRaiseHand : undefined,
        isNotVisible ? "Raise Hand" : undefined,
      );
    },
    [handleRaiseHand],
  );

  const toggleCamera = useCallback(() => {
    // If the user is in audio-only mode, don't allow them to turn on video
    if (isAudioOnly) {
      createDeviceToast(
        "Camera Disabled",
        "Your camera has been disabled because you are in audio-only mode",
        false,
        "camera",
      );
      return;
    }
    if (cameraError) {
      createDeviceErrorToast("camera", handleOpenAVPreferences);
      return;
    }
    avConnection?.setLocalVideo(mutedVideo);
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.TOGGLE_CAMERA,
        userId,
        contextData: { enabled: !mutedVideo },
      }),
    );
  }, [
    isAudioOnly,
    cameraError,
    avConnection,
    mutedVideo,
    dispatch,
    userId,
    handleOpenAVPreferences,
  ]);

  const togglePtt = useCallback(() => {
    if (micError) {
      createDeviceErrorToast("mic", handleOpenAVPreferences);
      return;
    }
    // A student can only unmute if they have video on screen
    // A professor can also unmute if they are in the breakout control room
    if (isMutedBecauseNotVisible) {
      createMicrophoneMutedToast(true);
      return;
    }
    if (!canTogglePtt) {
      createDeviceToast(
        "Unmuting Disabled",
        "Hold down the control key to temporarily unmute anytime",
        false,
        "mic",
      );
      return;
    }
    const newPttMode = !pttMode;
    dispatch(setPttModeForUser({ userId, pttMode: newPttMode }));
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.TOGGLE_MIC,
        userId,
        contextData: {
          enabled: !newPttMode,
          canTogglePtt,
        },
      }),
    );

    if (newPttMode) {
      createMicrophoneMutedToast();
    } else {
      createDeviceToast("Microphone unmuted", "You're now sharing your audio", true, "mic");
    }
  }, [
    micError,
    canTogglePtt,
    isMutedBecauseNotVisible,
    pttMode,
    dispatch,
    userId,
    handleOpenAVPreferences,
    createMicrophoneMutedToast,
  ]);

  // Update local audio whenever PTT mode changes
  useEffect(() => {
    avConnection?.setLocalAudio(!pttMode);
  }, [avConnection, pttMode]);

  useEffect(() => {
    if (hasActiveBreakoutSession && !wasInBreakout) {
      setWasInBreakout(true);
    } else if (!hasActiveBreakoutSession && wasInBreakout) {
      setWasInBreakout(false);
      if (!pttMode && !canControlClassroom) {
        dispatch(setPttModeForUser({ userId, pttMode: true }));
      }
    }
  }, [
    dispatch,
    wasInBreakout,
    setWasInBreakout,
    hasActiveBreakoutSession,
    canControlClassroom,
    pttMode,
    userId,
  ]);

  // Update PTT mode when there are changes to the conditions that allow going off PTT (breakouts, changes in the class)
  useEffect(() => {
    // If the stream is loading, we don't want to change ptt or notify the user
    if (isPublishedStreamLoading) {
      return;
    }
    if (!pttMode && !canTogglePtt) {
      createMicrophoneMutedToast(isMutedBecauseNotVisible);
      dispatch(setPttModeForUser({ userId, pttMode: true }));
    }
  }, [
    pttMode,
    canTogglePtt,
    isMutedBecauseNotVisible,
    dispatch,
    userId,
    avConnection,
    createMicrophoneMutedToast,
    isPublishedStreamLoading,
  ]);

  // Toggle audio when the user presses the PTT keyboard shortcut
  useEffect(() => {
    if (keyboardShortcutSignal === KeyboardShortcutSignalType.ToggleMute) {
      togglePtt();
      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
    }
  }, [dispatch, keyboardShortcutSignal, togglePtt]);

  return (
    <div className="flex">
      <CircleButton
        mode={isPublishedStreamLoading ? "red" : mutedAudio ? "red" : "dark"}
        onClick={togglePtt}
        IconComponent={mutedAudio ? MicMutedIcon : MicUnmutedIcon}
        tooltip={micTooltip}
        ariaLabel={micTooltip}
        disabled={isPublishedStreamLoading}
      />

      {canControlClassroom || allowStudentNoVideo ? (
        <CircleButton
          mode={isPublishedStreamLoading ? "red" : mutedVideo ? "red" : "dark"}
          onClick={toggleCamera}
          IconComponent={mutedVideo ? CameraOffIcon : CameraIcon}
          tooltip={camTooltip}
          ariaLabel={camTooltip}
          disabled={isPublishedStreamLoading}
        />
      ) : null}
    </div>
  );
}

export { AvControls };
